import React, { useState } from 'react';
import { Therapist, Session } from '../../../core/types';
import { useAppointmentsStore } from '../store/appointmentsStore';
import { useHomepageStore } from '../../homepage/store/homepageStore';
import { BottomSheet, LoadingWidget } from '../../../core/components/ui';
import toast from 'react-hot-toast';

interface Props {
  therapist: Therapist;
  onClose: () => void;
  onBooked: () => void;
}

const BookingSheet: React.FC<Props> = ({ therapist, onClose, onBooked }) => {
  const appStore = useAppointmentsStore();
  const homeStore = useHomepageStore();
  const user = homeStore.retrieve();

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const minDate = new Date().toISOString().split('T')[0];

  const handleBook = async () => {
    if (!date) { toast.error('Please select a date.'); return; }
    if (!time) { toast.error('Please select a time.'); return; }
    if (!reason.trim()) { toast.error('Please provide a reason for the session.'); return; }

    setLoading(true);

    // try {
    //   const sessionFrom = new Date(`${date}T${time}:00`).toISOString();
    //   const sessionTo = new Date(
    //     new Date(sessionFrom).getTime() + 60 * 60000
    //   ).toISOString();

    //   const sessionData: Session = {
    //     customerId: user?.id,
    //     therapistId: therapist.therapistId,
    //     therapistName: therapist.name,
    //     sessionFrom,
    //     sessionTo,
    //     sessionReason: reason.trim(),
    //     sessionDescription: description.trim(),
    //     sessionRate: therapist.rate,
    //     message: ''
    //   };

    //   const result = await appStore.bookSession(sessionData);

    //   // Therapist is already booked for this time slot
    //   if (result?.status === '409' || result?.error === true) {
    //     toast.error(
    //       result?.message ||
    //         'This therapist is already booked for the selected date and time. Please choose another slot.'
    //     );
    //     return;
    //   }

    //   // Successful booking
    //   if (result?.id || result?.status === '200') {
    //     toast.success('Session booked successfully!');
    //     onBooked();
    //     return;
    //   }

    //   toast.error('Booking failed. Please try again.');
    // } catch (error: any) {

    //   const message =
    //     error?.response?.data?.message ||
    //     'Booking failed. Please try again.';

    //   toast.error(message);
    // } finally {
    //   setLoading(false);
    // }

    try {
      const sessionFrom = new Date(`${date}T${time}:00`).toISOString();

      const sessionTo = new Date(
        new Date(sessionFrom).getTime() + 60 * 60000
      ).toISOString();

      const sessionData: Session = {
        customerId: user?.id,
        therapistId: therapist.therapistId,
        therapistName: therapist.name,
        sessionFrom,
        sessionTo,
        sessionReason: reason.trim(),
        sessionDescription: description.trim(),
        sessionRate: therapist.rate,
        profile_picture_url: therapist.url || '',
      };

      const result = await appStore.bookSession(sessionData);


      // Handle booking conflict
      if (result.status === 409 || result.error === true) {
        toast.error(
          result.message ||
          'This therapist is already booked for the selected date and time. Please choose another slot.'
        );
        return;
      }

      // Handle successful booking
      if (result.status === 201 && result.data) {
        toast.success(result.message || 'Session booked successfully!');

        onBooked();

        return;
      }

      // Unexpected response
      toast.error(result.message || 'Booking failed. Please try again.');
    } catch (error: any) {

      const errorData = error?.response?.data;

      if (error?.response?.status === 409) {
        toast.error(
          errorData?.message ||
          'This therapist is already booked for the selected date and time. Please choose another slot.'
        );
        return;
      }

      toast.error(
        errorData?.message ||
        'Booking failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <>
      {loading && <LoadingWidget />}
      <BottomSheet open onClose={onClose}>
        <div className="form-stack" style={{ padding: '24px 20px 40px' }}>
          {/* Handle */}
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-outline)', margin: '0 auto 20px' }} />

          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Book Session</h3>
          <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginBottom: 20 }}>
            with <strong>{therapist.name}</strong> · {therapist.rate} credit{therapist.rate !== 1 ? 's' : ''}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="field-label">Date</label>
              <input
                className="input"
                type="date"
                min={minDate}
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>

            <div>
              <label className="field-label">Time</label>
              <input
                className="input"
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
              />
            </div>

            <div>
              <label className="field-label">Reason for Session</label>
              <input
                className="input"
                placeholder="e.g. Anxiety management"
                value={reason}
                onChange={e => setReason(e.target.value)}
                maxLength={200}
              />
            </div>

            <div>
              <label className="field-label">Additional Notes (Optional)</label>
              <textarea
                className="input"
                style={{ minHeight: 80, resize: 'vertical' }}
                placeholder="Any additional context..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={500}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, height: 50, borderRadius: 8,
                  background: 'var(--color-surface-variant)',
                  color: 'var(--color-on-surface-variant)',
                  fontWeight: 600, fontSize: 15,
                }}
              >
                Cancel
              </button>
              <button
                className="btn-gradient"
                style={{ flex: 2 }}
                onClick={handleBook}
                disabled={loading}
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default BookingSheet;
